/**
 * Request Slabs Modal
 * ===================
 * Modal per richiedere disponibilità lastre e foto reali.
 * Sistema lead generation per connettere utenti con professionisti.
 * 
 * Campi:
 * - Nome, Email, Telefono (opzionale)
 * - Tipo richiesta (disponibilità / foto lastre)
 * - Quantità stimata
 * - Note progetto
 * - Messaggio (precompilato)
 */

import React, { useState, useEffect } from 'react';
import { X, Send, User, Mail, Phone, Package, FileText, CheckCircle, Camera, Layers, AlertCircle } from 'lucide-react';

const REQUEST_TYPES = [
  {
    id: 'availability',
    label: 'Verifica Disponibilità',
    icon: Package,
    description: 'Controlla se il materiale è disponibile e i tempi di consegna'
  },
  {
    id: 'slab_photos',
    label: 'Foto Lastre Reali',
    icon: Camera,
    description: 'Richiedi foto delle lastre effettivamente in stock'
  },
  {
    id: 'quote',
    label: 'Preventivo',
    icon: FileText,
    description: 'Richiedi un preventivo personalizzato per il tuo progetto'
  }
];

const QUANTITY_OPTIONS = [
  { value: 'small', label: '1-5 mq', description: 'Bagno, top cucina piccolo' },
  { value: 'medium', label: '5-20 mq', description: 'Cucina completa, bagno grande' },
  { value: 'large', label: '20-50 mq', description: 'Pavimento, rivestimento' },
  { value: 'project', label: '50+ mq', description: 'Progetto commerciale' }
];

const RequestSlabsModal = ({ 
  isOpen, 
  onClose, 
  material,
  user = null
}) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    request_type: 'availability',
    quantity: 'medium',
    project_notes: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState(null);
  
  // Pre-fill user data and message
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        name: user.full_name || user.email?.split('@')[0] || '',
        email: user.email || ''
      }));
    }
    
    if (material) {
      const defaultMessage = `Buongiorno,\n\nSono interessato al materiale "${material.name}" (${material.category || material.stone_type || 'Pietra Naturale'}).\n\nVorrei ricevere informazioni sulla disponibilità e le caratteristiche delle lastre.\n\nGrazie.`;
      setFormData(prev => ({
        ...prev,
        message: defaultMessage
      }));
    }
  }, [user, material]);
  
  if (!isOpen) return null;
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    
    try {
      // Prepare request data
      const requestData = {
        material_id: material?.id,
        material_name: material?.name,
        user_id: user?.id || null,
        requester_name: formData.name,
        requester_email: formData.email,
        requester_phone: formData.phone || null,
        request_type: formData.request_type,
        quantity_estimate: formData.quantity,
        project_notes: formData.project_notes || null,
        message: formData.message
      };
      
      console.log('📤 Submitting material request via API:', requestData);
      
      // Use backend API endpoint instead of direct Supabase (bypasses RLS)
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${API_URL}/api/professional/material-request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestData)
      });
      
      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.detail || 'Errore durante l\'invio');
      }
      
      console.log('✅ Request saved:', result);
      setIsSuccess(true);
      
      // Auto-close after success
      setTimeout(() => {
        onClose();
        setIsSuccess(false);
        // Reset form
        setFormData({
          name: user?.full_name || '',
          email: user?.email || '',
          phone: '',
          request_type: 'availability',
          quantity: 'medium',
          project_notes: '',
          message: ''
        });
      }, 3000);
      
    } catch (err) {
      console.error('❌ Submit error:', err);
      setError(err.message || 'Errore durante l\'invio. Riprova.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  // Success State
  if (isSuccess) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
        <div className="relative bg-[#1A1A1A] rounded-xl w-full max-w-md p-8 text-center">
          <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-500" />
          </div>
          <h3 className="text-xl font-semibold text-white mb-2">Richiesta Inviata!</h3>
          <p className="text-[#A0A0A0] mb-4">
            Ti metteremo in contatto con un professionista certificato nella tua zona.
          </p>
          <p className="text-sm text-[#808080]">
            Riceverai una risposta entro 24-48 ore lavorative.
          </p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/80 backdrop-blur-sm" 
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-[#1A1A1A] rounded-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-[#2A2A2A] flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-white">Richiedi Lastre</h2>
            <p className="text-sm text-[#808080]">Connettiti con professionisti certificati</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-[#2A2A2A] rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-[#808080]" />
          </button>
        </div>
        
        {/* Material Preview */}
        {material && (
          <div className="px-6 py-4 bg-[#0A0A0A] border-b border-[#2A2A2A] flex-shrink-0">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-lg overflow-hidden bg-[#2A2A2A] flex-shrink-0">
                {material.image_main ? (
                  <img 
                    src={material.image_main} 
                    alt={material.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Layers className="w-6 h-6 text-[#666]" />
                  </div>
                )}
              </div>
              <div>
                <h3 className="text-white font-medium">{material.name}</h3>
                <p className="text-sm text-[#808080]">
                  {material.category || material.stone_type} • {material.origin || 'Premium Selection'}
                </p>
              </div>
            </div>
          </div>
        )}
        
        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">
            {/* Error Message */}
            {error && (
              <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-400">{error}</p>
              </div>
            )}
            
            {/* Request Type */}
            <div>
              <label className="block text-sm font-medium text-[#A0A0A0] mb-3">
                Tipo di Richiesta
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {REQUEST_TYPES.map((type) => {
                  const Icon = type.icon;
                  const isSelected = formData.request_type === type.id;
                  return (
                    <button
                      key={type.id}
                      type="button"
                      onClick={() => handleChange('request_type', type.id)}
                      className={`p-4 rounded-lg border text-left transition-all ${
                        isSelected
                          ? 'border-[#D4AF37] bg-[#D4AF37]/10'
                          : 'border-[#2A2A2A] hover:border-[#3A3A3A] bg-[#0A0A0A]'
                      }`}
                    >
                      <Icon className={`w-5 h-5 mb-2 ${isSelected ? 'text-[#D4AF37]' : 'text-[#808080]'}`} />
                      <p className={`text-sm font-medium ${isSelected ? 'text-white' : 'text-[#A0A0A0]'}`}>
                        {type.label}
                      </p>
                      <p className="text-xs text-[#666] mt-1">{type.description}</p>
                    </button>
                  );
                })}
              </div>
            </div>
            
            {/* Contact Info */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#A0A0A0] mb-2">
                  Nome *
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666]" />
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    required
                    className="w-full pl-10 pr-4 py-3 bg-[#0A0A0A] border border-[#2A2A2A] rounded-lg text-white placeholder-[#666] focus:border-[#D4AF37] focus:outline-none transition-colors"
                    placeholder="Il tuo nome"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#A0A0A0] mb-2">
                  Email *
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666]" />
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    required
                    className="w-full pl-10 pr-4 py-3 bg-[#0A0A0A] border border-[#2A2A2A] rounded-lg text-white placeholder-[#666] focus:border-[#D4AF37] focus:outline-none transition-colors"
                    placeholder="email@esempio.com"
                  />
                </div>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-[#A0A0A0] mb-2">
                Telefono (opzionale)
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666]" />
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-[#0A0A0A] border border-[#2A2A2A] rounded-lg text-white placeholder-[#666] focus:border-[#D4AF37] focus:outline-none transition-colors"
                  placeholder="+39 000 000 0000"
                />
              </div>
            </div>
            
            {/* Quantity */}
            <div>
              <label className="block text-sm font-medium text-[#A0A0A0] mb-3">
                Quantità Stimata
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {QUANTITY_OPTIONS.map((opt) => {
                  const isSelected = formData.quantity === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => handleChange('quantity', opt.value)}
                      className={`p-3 rounded-lg border text-center transition-all ${
                        isSelected
                          ? 'border-[#D4AF37] bg-[#D4AF37]/10'
                          : 'border-[#2A2A2A] hover:border-[#3A3A3A] bg-[#0A0A0A]'
                      }`}
                    >
                      <p className={`text-sm font-medium ${isSelected ? 'text-white' : 'text-[#A0A0A0]'}`}>
                        {opt.label}
                      </p>
                      <p className="text-[10px] text-[#666] mt-0.5">{opt.description}</p>
                    </button>
                  );
                })}
              </div>
            </div>
            
            {/* Project Notes */}
            <div>
              <label className="block text-sm font-medium text-[#A0A0A0] mb-2">
                Note Progetto (opzionale)
              </label>
              <input
                type="text"
                value={formData.project_notes}
                onChange={(e) => handleChange('project_notes', e.target.value)}
                className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#2A2A2A] rounded-lg text-white placeholder-[#666] focus:border-[#D4AF37] focus:outline-none transition-colors"
                placeholder="es. Ristrutturazione cucina, nuovo showroom..."
              />
            </div>
            
            {/* Message */}
            <div>
              <label className="block text-sm font-medium text-[#A0A0A0] mb-2">
                Messaggio
              </label>
              <textarea
                value={formData.message}
                onChange={(e) => handleChange('message', e.target.value)}
                rows={4}
                className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#2A2A2A] rounded-lg text-white placeholder-[#666] focus:border-[#D4AF37] focus:outline-none transition-colors resize-none"
                placeholder="Descrivi la tua richiesta..."
              />
            </div>
          </div>
          
          {/* Footer */}
          <div className="px-6 py-4 border-t border-[#2A2A2A] bg-[#0A0A0A] flex-shrink-0">
            <div className="flex items-center justify-between gap-4">
              <p className="text-xs text-[#666]">
                Ti metteremo in contatto con professionisti certificati nella tua zona
              </p>
              <button
                type="submit"
                disabled={isSubmitting || !formData.name || !formData.email}
                className="px-6 py-3 bg-[#D4AF37] hover:bg-[#B8963A] disabled:bg-[#D4AF37]/50 disabled:cursor-not-allowed text-[#0A0A0A] font-medium rounded-lg flex items-center gap-2 transition-colors"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-[#0A0A0A]/30 border-t-[#0A0A0A] rounded-full animate-spin" />
                    Invio...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Invia Richiesta
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RequestSlabsModal;
